import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { generateDeviceFingerprint } from "../utils/deviceFingerprint";

// Add CSS styles for the new design
const styles = `
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .reveal-line { opacity: 0; animation: fadeUp 700ms ease-out forwards; }
  .d0 { animation-delay: 0s; }
  .d1 { animation-delay: .18s; }
  .d2 { animation-delay: .36s; }
  .d3 { animation-delay: .54s; }

  .yellow-panel {
    position: relative;
    background: radial-gradient(circle at 25% 20%, rgba(255,255,255,0.25), transparent 70%),
                linear-gradient(135deg, #fef08a, #facc15, #eab308);
  }

  .yellow-panel::before {
    content: '';
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(45deg, rgba(255,255,255,0.05) 0 3px, transparent 3px 12px);
    opacity: 0.25;
    mix-blend-mode: overlay;
  }

  .yellow-panel::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 80% 70%, rgba(255,180,0,0.3), transparent 70%),
                linear-gradient(to bottom right, rgba(255,255,255,0.15), rgba(0,0,0,0.1));
    opacity: 0.4;
    mix-blend-mode: soft-light;
  }

  .login-container {
    min-height: 100vh;
    background-color: #000;
    color: #fff;
    overflow: hidden;
  }

  .login-form-section {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #000;
    color: #fff;
    padding: 2rem;
    width: 100%;
    min-height: 100vh;
  }

  .login-form-content {
    width: 100%;
    max-width: 28rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .logo-container {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .logo {
    max-height: 12rem;
    width: auto;
    user-select: none;
  }

  .form-group {
    display: flex;
    flex-direction: column;
  }

  .form-label {
    margin-bottom: 0.5rem;
    text-align: left;
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
  }

  .form-input {
    height: 3rem;
    width: 100%;
    border-radius: 0.75rem;
    border: 1px solid #404040;
    background-color: rgba(23, 23, 23, 0.9);
    padding: 0 1rem;
    font-size: 0.875rem;
    color: #fff;
    outline: none;
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2);
    transition: all 0.2s;
    box-sizing: border-box;
  }

  .form-input:focus {
    ring: 2px;
    ring-color: #facc15;
    border-color: #facc15;
  }

  .form-input::placeholder {
    color: #9ca3af;
  }

  .login-button {
    position: relative;
    display: flex;
    height: 3rem;
    width: 100%;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    border-radius: 0.75rem;
    background-color: #facc15;
    padding: 0 1rem;
    font-size: 0.875rem;
    font-weight: bold;
    color: #000;
    transition: all 0.2s;
    border: none;
    cursor: pointer;
    box-sizing: border-box;
  }

  .login-button:hover {
    background-color: #fde047;
  }

  .login-button:active {
    transform: translateY(1px);
  }

  .login-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .yellow-panel-section {
    position: relative;
    display: none;
    width: 100%;
    overflow: hidden;
    color: #000;
  }

  .yellow-panel-content {
    position: relative;
    z-index: 10;
    margin: 0 auto;
    display: flex;
    height: 100%;
    max-width: 48rem;
    flex-direction: column;
    justify-content: center;
    padding: 3rem 3rem 4rem 3rem;
  }

  .hero-title {
    margin-top: 1rem;
    font-size: 3.75rem;
    font-weight: bold;
    line-height: 1.05;
    letter-spacing: -0.02em;
    text-shadow: 0 2px 4px rgba(0,0,0,0.25);
  }

  .hero-line {
    display: block;
    text-align: left;
    font-weight: bold;
  }

  .footer-section {
    position: absolute;
    bottom: 1rem;
    left: 2.5rem;
    right: 2.5rem;
    z-index: 10;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: rgba(0, 0, 0, 0.8);
  }

  .powered-by {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .powered-by-text {
    font-size: 1rem;
    font-weight: 600;
  }

  .powered-by-logo {
    height: 2rem;
    width: auto;
  }

  .copyright {
    font-size: 1rem;
    font-weight: 600;
  }

  .error-message {
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    color: #dc2626;
    padding: 0.75rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    margin-top: 0.5rem;
  }

  .warning-message {
    background-color: #fffbeb;
    border: 1px solid #fed7aa;
    color: #d97706;
    padding: 0.75rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    margin-top: 0.5rem;
    white-space: pre-line;
    line-height: 1.4;
  }

  .decorative-svg {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 50%;
    color: rgba(245, 158, 11, 0.25);
  }

  /* Mobile and Tablet styles - Hide yellow panel */
  @media (max-width: 1023px) {
    .login-form-section {
      width: 100%;
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .yellow-panel-section {
      display: none;
    }
    
    .login-form-content {
      max-width: 24rem;
      width: 100%;
    }
    
    .logo {
      max-height: 8rem;
    }
  }

  /* Mobile specific styles */
  @media (max-width: 480px) {
    .login-form-section {
      padding: 1rem;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .login-form-content {
      padding: 1rem;
      max-width: 20rem;
      width: 100%;
    }
    
    .logo {
      max-height: 6rem;
    }
    
    .form-input {
      height: 2.75rem;
      font-size: 16px; /* Prevents zoom on iOS */
    }
    
    .login-button {
      height: 2.75rem;
      font-size: 16px;
    }
  }

  /* Desktop styles - Show yellow panel */
  @media (min-width: 1024px) {
    .login-form-section {
      width: 35%;
      min-height: 100vh;
    }
    
    .yellow-panel-section {
      display: block;
      width: 65%;
    }
    
    .hero-title {
      font-size: 4.5rem;
    }
    
    .powered-by-logo {
      height: 2.5rem;
    }
  }
`;

const Login = () => {
  const baseURL = process.env.REACT_APP_API_URL;
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const navigate = useNavigate();

  const validateForm = React.useCallback(() => {
    if (username.length === 0 || password.length === 0) {
      setError("");
      setWarning("Username and password are required");
      return false;
    }
    setWarning("");
    return true;
  }, [username, password]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setError("");
    setWarning("");

    try {
      const deviceData = await generateDeviceFingerprint();
      const formDetails = new URLSearchParams();
      formDetails.append("username", username);
      formDetails.append("password", password);
      formDetails.append("device_data", JSON.stringify(deviceData));

      const response = await fetch(`${baseURL}/auth/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formDetails,
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("token", data.access_token);
        localStorage.setItem("user", JSON.stringify(data.user));

        if (data.user.is_admin) {
          navigate("/admin/dashboard");
        } else {
          navigate("/");
        }
      } else {
        let errorMessage = "Authentication failed!";
        try {
          const errorData = await response.json();
          const detail = errorData.detail;
          if (typeof detail === "string") {
            errorMessage = detail;
          } else if (Array.isArray(detail)) {
            errorMessage = detail.map((d: { msg?: string }) => d.msg).join(" ");
          }
        } catch {
          /* ignore parse errors */
        }

        if (
          response.status === 403 &&
          errorMessage.includes("Device not recognized")
        ) {
          setWarning(
            "⚠️ Device Access Restricted\n\n" +
              "You are trying to login from a different device than the one you registered with. " +
              "For security reasons, you can only login from the same device you used during your first login.\n\n" +
              "If you need to access your account from a different device, please contact your administrator."
          );
        } else {
          setError(errorMessage);
        }
        setLoading(false);
      }
    } catch {
      setLoading(false);
      setError("An error occurred, Please try again later.");
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="login-container">
        <div style={{ display: 'flex', minHeight: '100vh', flexDirection: isTablet ? 'column' : 'row' }}>
          
          {/* Login Form Section */}
          <div className="login-form-section">
            <div className="login-form-content">
              <div className="logo-container">
            <img
              src="./logo-2.png" 
                  alt="Auto Kallugjeri" 
                  className="logo"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div className="form-group">
                  <label htmlFor="username" className="form-label">Username</label>
                    <input
                    id="username"
                    name="username"
                    type="text"
                      required
                    className="form-input"
                    placeholder="Enter your username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                  <label htmlFor="password" className="form-label">Password</label>
                    <input
                    id="password"
                    name="password"
                    type="password"
                      required
                    className="form-input"
                    placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>

                  <button
                    type="submit"
                  className="login-button"
                    disabled={loading}
                >
                  {loading ? "Loading..." : "Sign in"}
                  </button>

                    {error && (
                  <div className="error-message">
                        {error}
                  </div>
                    )}
                    {warning && (
                  <div className="warning-message">
                        {warning}
                  </div>
                    )}
                </form>
              </div>
            </div>

          {/* Yellow Panel Section - Hidden on mobile */}
          {!isTablet && (
            <div className="yellow-panel-section yellow-panel">
              <svg 
                className="decorative-svg" 
                viewBox="0 0 100 100" 
                preserveAspectRatio="none" 
                aria-hidden="true"
              >
                <polygon fill="currentColor" points="0,0 100,0 0,100"></polygon>
              </svg>

              <div className="yellow-panel-content">
                <h1 className="hero-title">
                  <span className="hero-line reveal-line d0">WE WORK HARD</span>
                  <span className="hero-line reveal-line d1">TO BRING YOU</span>
                  <span className="hero-line reveal-line d2">THE BEST</span>
                  <span className="hero-line reveal-line d3">SERVICE</span>
                </h1>
              </div>

              <div className="footer-section">
                <div className="powered-by">
                  <span className="powered-by-text">Powered by</span>
              <img
                src="./tecm_logo.png"
                    alt="Tecmotion Logo" 
                    className="powered-by-logo"
                  />
                </div>
                <p className="copyright">© 2025 Auto Kallugjeri</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Login;
